import React, { useCallback, useMemo, useState } from 'react';
import { checkPrinter, print } from 'services/printer.service';
import { confirmPrint, getContent } from 'services/shipment-labels.service';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import { useAlert } from 'context/AlertProvider';
import { useZebra } from 'context/ZebraProvider';
import { Button, Confirm, Popup } from 'semantic-ui-react';
import { runSequentially } from 'modules/run-sequentially/run-sequentially';
import {
  isStock,
  isWarehouseManager,
} from 'modules/user-has-permission/user-has-permission';
import { STATUSES } from 'constants/constants';

export default function PalletPrintingButton({
  row,
  preparationOrder,
  isBatchPrinting,
  setIsBatchPrinting = () => {},
  onBatchPrintingFinished = () => {},
}) {
  const alert = useAlert();
  const zebra = useZebra();
  const [confirmIsVisible, setConfirmIsVisible] = useState(false);
  const palletInProgress = useMemo(
    () => preparationOrder.not_manifested?.some((agency) => !!agency.count),
    [preparationOrder],
  );
  const canNotPrint = useCallback(
    (printablePallets) => {
      return (
        !!preparationOrder.has_external_labels ||
        isWarehouseManager() ||
        isStock() ||
        preparationOrder.status === STATUSES.CLOSED.name ||
        isBatchPrinting ||
        !printablePallets ||
        palletInProgress
      );
    },
    [isBatchPrinting, palletInProgress, preparationOrder],
  );

  const handlePalletPrinting = useCallback(
    async (units_per_pallet, ean) => {
      setIsBatchPrinting(true);
      const isAvailable = zebra ? await checkPrinter(zebra) : false;
      if (!isAvailable) {
        alert.current.error(
          'Parece que la impresora por defecto no esta disponible, revise que esta encendida y conectada e intentelo de nuevo.',
        );
        return;
      }

      const label_ids = preparationOrder?.labels
        .filter((label) => label.ean === ean)
        .slice(0, units_per_pallet)
        .map((label) => label.label_id);

      try {
        await runSequentially(
          label_ids,
          async (label_id) => {
            try {
              const {
                data: { content },
              } = await getContent(label_id);
              await print(zebra, content);
              await confirmPrint(label_id);
            } catch (error) {
              throw error;
            }
          },
          {
            msBetweenEachRun: 2000,
          },
        );
        const separatorLabel = `^XA\\n^FX\\n^CFA,40\\n^A0N,50,40^FO50,100^FDREFERENCIA^FS\\n^A0N,40,32^FO50,180^FD${preparationOrder?.reference}^FS\\n^A0N,50,40^FO50,300^FDLOTE^FS\\n^A0N,40,40^FO50,380^FD${units_per_pallet} ETIQUETAS^FS\\n^A0N,50,40^FO50,500^FDEAN^FS\\n^A0N,40,40^FO50,580^FD${ean}^FS\\n^XZ`;
        await print(zebra, separatorLabel);
        onBatchPrintingFinished(true);
        setIsBatchPrinting(false);
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
        onBatchPrintingFinished();
        setIsBatchPrinting(false);
      }
    },
    [
      setIsBatchPrinting,
      preparationOrder,
      zebra,
      alert,
      onBatchPrintingFinished,
    ],
  );

  const renderPrintPallet = useCallback(
    ({ units_per_pallet, pendingLabels, ean }) => {
      const printablePallets = Math.trunc(pendingLabels / units_per_pallet);

      return (
        <Popup
          className="base-tooltip"
          content="Imprimir 1 pallet"
          trigger={
            <div>
              <Button
                circular
                disabled={canNotPrint(printablePallets)}
                loading={isBatchPrinting}
                icon="print"
                content={printablePallets}
                onClick={(event) => setConfirmIsVisible(true)}
              />
              <Confirm
                open={confirmIsVisible}
                content={`¿Confirmas la impresión en lote? Se imprimirán ${units_per_pallet} etiquetas seguidas`}
                cancelButton="Cancelar"
                confirmButton={{
                  content: `Imprimir ${units_per_pallet} etiquetas`,
                  autoFocus: true,
                }}
                onCancel={(event, data) => setConfirmIsVisible(false)}
                onConfirm={() => handlePalletPrinting(units_per_pallet, ean)}
              />
            </div>
          }
        />
      );
    },
    [canNotPrint, confirmIsVisible, handlePalletPrinting, isBatchPrinting],
  );

  return <>{renderPrintPallet(row)}</>;
}
